<template>
  <b-modal
    :visible="visible"
    no-close-on-backdrop
    no-close-on-esc
    size="lg"
    @hide="close"
  >
    <template #modal-header>
      <div class="d-flex w-100 border-bottom pb-2">
        <div class="w-100 text-left">
          <h2 class="mb-1">Alterar em lote</h2>
          <p class="mb-0 text-muted">
            {{ slotLabel }}
          </p>
        </div>
        <button
          type="button"
          class="close"
          aria-label="Fechar"
          @click.prevent="close"
        >
          <span class="no no-e-remove"></span>
        </button>
      </div>
    </template>

    <h4 class="booking-batch-title">
      Reservas ({{ selectedHashes.length }}/{{ bookings.length }})
      <a href="#" class="booking-batch-toggle" @click.prevent="toggleAll">
        {{ allSelected ? 'desmarcar todas' : 'marcar todas' }}
      </a>
    </h4>
    <b-form-checkbox
      v-for="booking in bookings"
      :key="booking.hash"
      :checked="selectedHashes.includes(booking.hash)"
      class="booking-batch-option"
      @change="toggleBooking(booking.hash, $event)"
    >
      <span class="font-weight-bold">#{{ booking.code }}</span>
      <span> · {{ customerLabel(booking) }}</span>
      <div class="text-muted booking-batch-current">
        <span v-if="vehicles.length">
          {{ currentVehicleLabel(booking) }} ·
        </span>
        {{ currentDriverName(booking) || 'Sem condutor' }}
      </div>
    </b-form-checkbox>

    <div v-if="vehicles.length" class="mt-4">
      <h4 class="booking-batch-title">Carro</h4>
      <el-select
        v-model="selectedVehicleKey"
        clearable
        placeholder="Manter o carro atual"
        class="w-100"
      >
        <el-option
          v-for="vehicle in vehicles"
          :key="vehicle.key"
          :label="`${vehicle.name} — ${vehicle.occupied}/${vehicle.capacity}`"
          :value="vehicle.key"
        />
      </el-select>
      <p
        v-if="selectedVehicle"
        :class="[
          'mb-0 mt-2',
          overCapacity ? 'text-danger' : 'text-muted',
        ]"
      >
        Ocupação depois do lote:
        <b>{{ vehicleOccupancy }}/{{ selectedVehicle.capacity }}</b>
        <span v-if="overCapacity">
          · passa {{ vehicleOccupancy - selectedVehicle.capacity }} da
          capacidade. Desmarque reservas ou escolha outro carro.
        </span>
      </p>
      <ul v-if="movedBookings.length" class="booking-batch-moves mb-0 mt-2">
        <li v-for="booking in movedBookings" :key="booking.hash">
          #{{ booking.code }} sai de {{ currentVehicleLabel(booking) }} e vai
          inteira para {{ selectedVehicle.name }}
        </li>
      </ul>
    </div>

    <div v-if="selectedVehicle" class="mt-4">
      <h4 class="booking-batch-title">Motorista do {{ selectedVehicle.name }}</h4>
      <el-select
        v-model="selectedDriverUuid"
        filterable
        clearable
        :loading="loadingAgents"
        placeholder="Sem motorista"
        class="w-100"
      >
        <el-option
          v-for="agent in activeAgents"
          :key="agent.uuid"
          :label="agent.nickname ? `${agent.name} (${agent.nickname})` : agent.name"
          :value="agent.uuid"
        />
      </el-select>
      <p class="mb-0 mt-2 text-muted booking-batch-hint">
        O motorista é da saída (carro + horário): não ocupa vaga e é gravado nas
        reservas deste carro.
      </p>
    </div>

    <div v-else class="mt-4">
      <h4 class="booking-batch-title">Condutores das reservas</h4>
      <el-select
        v-model="selectedAgentUuids"
        multiple
        filterable
        clearable
        :loading="loadingAgents"
        placeholder="Manter os condutores atuais"
        class="w-100"
      >
        <el-option
          v-for="agent in activeAgents"
          :key="agent.uuid"
          :label="agent.nickname ? `${agent.name} (${agent.nickname})` : agent.name"
          :value="agent.uuid"
        />
      </el-select>
      <p class="mb-0 mt-2 text-muted booking-batch-hint">
        Condutor que não dirige nenhum carro do horário viaja como passageiro e
        ocupa um lugar. Para definir quem dirige, escolha um carro acima.
      </p>
      <p
        v-if="!loadingAgents && !activeAgents.length"
        class="mb-0 mt-2 text-muted"
      >
        Nenhum condutor ativo encontrado.
      </p>
    </div>

    <template #modal-footer>
      <base-button type="secondary" @click="close">Cancelar</base-button>
      <base-button
        type="primary"
        :disabled="saving || !canConfirm"
        @click="confirm"
      >
        {{
          !saving
            ? 'Confirmar'
            : progress
            ? `Salvando ${progress.label}: ${progress.done} de ${progress.total}…`
            : 'Salvando...'
        }}
      </base-button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'BookingBatchAssignModal',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    slotMeta: {
      type: Object,
      default: null,
    },
    bookings: {
      type: Array,
      default: () => [],
    },
    vehicles: {
      type: Array,
      default: () => [],
    },
    // hash -> ['Safari 01 (10)', 'A distribuir (2)'], montado pelo painel de carros
    allocationLabels: {
      type: Object,
      default: () => ({}),
    },
    saving: {
      type: Boolean,
      default: false,
    },
    // hash -> lugares (pessoas + condutores extras), montado pelo painel
    seatsByHash: {
      type: Object,
      default: () => ({}),
    },
    // { label, done, total } enquanto o lote grava em blocos
    progress: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loadingAgents: false,
      selectedHashes: [],
      selectedVehicleKey: '',
      selectedAgentUuids: [],
      selectedDriverUuid: '',
    }
  },

  computed: {
    ...mapGetters('agent', {
      agents: 'getAgents',
    }),

    slotLabel() {
      const slot = this.slotMeta || {}
      // "2026-10-27 09:00" -> "Ter · 27/10 · 09h"
      const parsed = slot.block
        ? moment(slot.block, 'YYYY-MM-DD HH:mm')
        : null
      const weekday = parsed && parsed.isValid() ? parsed.format('ddd') : ''
      const weekdayLabel = weekday
        ? weekday.charAt(0).toUpperCase() + weekday.slice(1)
        : ''
      const block =
        parsed && parsed.isValid()
          ? `${weekdayLabel} · ${parsed
              .format('DD/MM · HH[h]mm')
              .replace(/h00$/, 'h')}`
          : ''
      return [slot.productName || slot.placeName, block]
        .filter(Boolean)
        .join(' · ')
    },

    activeAgents() {
      return (this.agents || []).filter(
        (agent) => agent.active === true || agent.active === 'true'
      )
    },

    allSelected() {
      return this.selectedHashes.length === this.bookings.length
    },

    selectedVehicle() {
      return (
        this.vehicles.find((item) => item.key === this.selectedVehicleKey) ||
        null
      )
    },

    selectedBookings() {
      return this.bookings.filter((booking) =>
        this.selectedHashes.includes(booking.hash)
      )
    },

    // O lote move a reserva inteira para o carro escolhido (ADR 001)
    vehicleOccupancy() {
      if (!this.selectedVehicle) return 0
      const { paxByHash } = this.selectedVehicle
      return this.selectedBookings.reduce(
        (sum, booking) =>
          sum + this.peopleCount(booking) - (paxByHash[booking.hash] || 0),
        this.selectedVehicle.occupied
      )
    },

    movedBookings() {
      if (!this.selectedVehicle) return []
      const { paxByHash } = this.selectedVehicle
      return this.selectedBookings.filter((booking) => {
        const labels = this.allocationLabels[booking.hash] || []
        const fullyHere = paxByHash[booking.hash] === this.peopleCount(booking)
        return labels.length && !fullyHere
      })
    },

    // Capacidade do carro é limite, não aviso (ADR 001)
    overCapacity() {
      return (
        !!this.selectedVehicle &&
        this.vehicleOccupancy > this.selectedVehicle.capacity
      )
    },

    currentVehicleDriverUuid() {
      const driver = this.selectedVehicle && this.selectedVehicle.driver
      return (driver && driver.uuid) || ''
    },

    driverChanged() {
      return (
        !!this.selectedVehicle &&
        this.selectedDriverUuid !== this.currentVehicleDriverUuid
      )
    },

    canConfirm() {
      return (
        this.selectedHashes.length > 0 &&
        (this.driverChanged ||
          !!this.selectedVehicle ||
          this.selectedAgentUuids.length > 0) &&
        !this.overCapacity
      )
    },
  },

  watch: {
    // Carro escolhido: o campo vira o motorista dele, começando pelo atual
    selectedVehicleKey() {
      this.selectedDriverUuid = this.currentVehicleDriverUuid
    },

    visible: {
      immediate: true,
      handler(value) {
        if (!value) return
        this.selectedHashes = []
        this.selectedVehicleKey = ''
        this.selectedAgentUuids = []
        this.loadAgents()
      },
    },
  },

  methods: {
    ...mapActions('agent', ['getAllAgents']),

    async loadAgents() {
      if (this.activeAgents.length) return
      this.loadingAgents = true
      await this.getAllAgents()
      this.loadingAgents = false
    },

    // Lugares da reserva (pessoas + condutores extras) quando o painel informa
    peopleCount(booking) {
      if (this.seatsByHash[booking.hash] != null) {
        return this.seatsByHash[booking.hash]
      }
      const tableText = booking.people && booking.people.tableText
      return (tableText && Number(tableText.count)) || 0
    },

    customerLabel(booking) {
      const tableText = booking.people && booking.people.tableText
      return (tableText && tableText.line1) || 'nome não informado'
    },

    currentVehicleLabel(booking) {
      const labels = this.allocationLabels[booking.hash]
      if (labels && labels.length) return labels.join(' + ')
      const active = (booking.vehicles || []).filter((item) => item.active)
      return active.length
        ? active.map((item) => item.name).join(' + ')
        : 'Sem carro'
    },

    currentDriverName(booking) {
      return (booking.agents || [])
        .filter(
          (item) =>
            (item.type === undefined || item.type === 'driver') &&
            item.active !== false
        )
        .map((item) => item.name)
        .join(', ')
    },

    toggleAll() {
      this.selectedHashes = this.allSelected
        ? []
        : this.bookings.map((booking) => booking.hash)
    },

    toggleBooking(hash, checked) {
      this.selectedHashes = checked
        ? this.selectedHashes.concat(hash)
        : this.selectedHashes.filter((item) => item !== hash)
    },

    close() {
      if (this.saving) return
      this.$emit('close')
    },

    confirm() {
      const agents = this.activeAgents
        .filter((agent) => this.selectedAgentUuids.includes(agent.uuid))
        .map((agent) => ({
          active: true,
          name: agent.name,
          uuid: agent.uuid,
          nickname: agent.nickname,
          place: agent.Place && agent.Place.name,
        }))

      this.$emit('confirm', {
        hash: this.selectedHashes.slice(),
        // Sem pax: a API aloca a reserva inteira e desativa os outros carros
        vehicles: this.selectedVehicle
          ? [{ resource_uuid: this.selectedVehicle.key }]
          : [],
        // Com carro escolhido, condutor = motorista da saída (vai no resources)
        agents: this.selectedVehicle ? [] : agents,
        driver: this.driverChanged
          ? {
              vehicleKey: this.selectedVehicle.key,
              uuid: this.selectedDriverUuid || null,
            }
          : null,
      })
    },
  },
}
</script>

<style scoped>
.booking-batch-title {
  margin-bottom: 0.5rem;
}

.booking-batch-toggle {
  margin-left: 0.5rem;
  font-size: 0.8rem;
  font-weight: normal;
}

.booking-batch-option {
  margin-bottom: 1rem;
}

/* O tema fixa height: 1rem no label do checkbox; a segunda linha da reserva
   encostava na reserva seguinte. Mesmo seletor do tema, prefixado. */
.booking-batch-option.custom-checkbox
  >>> .custom-control-input
  ~ .custom-control-label {
  height: auto;
}

.booking-batch-hint {
  font-size: 0.85rem;
}

.booking-batch-current {
  font-size: 0.85rem;
}

.booking-batch-moves {
  padding-left: 1.1rem;
  color: #b56b12;
  font-size: 0.85rem;
}
</style>
